import { ROUTE_NAME, STORE } from '~/constants/strings'
import type {
  WorkforceRepresentationStackedBarChart,
  WorkforceRepresentationBarChart,
  WorkforceRepresentationDoughnutChart,
  WorkforceRepresentationTiles,
  WorkforceRepresentationData,
  WorkforceRepresentationListProperty,
} from '~/constants/types/projectDataVisualizations'
import { useProjectDataVisualizationsStore } from './projectDataVisualizations'
import type {
  DataVizTileWorkforceRepresentationTileData,
  QueriesDecileSalaryRanges,
  QueriesGetWorkforceRepresentationRequest,
  QueriesGetWorkforceRepresentationResponse,
  QueriesWorkforceChartGroupData,
  QueriesWorkforceRepresentationCharts,
} from 'client/sratool'
import { api } from '~/services'
import { HNRV2 } from '~/helpers'
import { AxiosError } from 'axios'
import { useChartRefresh } from '~/composables/chart/useChartRefresh'
import { GET_DATA_FAILED } from '~/constants/projectDataVisualizations'
import { formatAsCurrency } from '~/helpers/number'
import { useChartHasDataCheck } from '~/composables/chart/useChartHasDataCheck'
import {
  useProjectDataVisualizationSelectionStore,
  type SelectionSnapshot,
} from './projectDataVisualizationsSelection'

export const useWorkforceInsightsDataVisualizationsStore = defineStore(
  STORE.WORKFORCE_REPRESENTATION_DATA_VISUALIZATIONS,
  () => {
    const wfaAPI = api.workforceAnalyticsDataVisualizations
    const ProjectDataVisualizationsStore = useProjectDataVisualizationsStore()
    const { isCurrent, getAnalysisData } =
      useProjectDataVisualizationSelectionStore()
    const { dataIsDefined, hasData } = useChartHasDataCheck()

    const data = ref<QueriesGetWorkforceRepresentationResponse>()

    const getTilesAndBarCharts = async (
      selection: SelectionSnapshot,
      requestValidation?: typeof isCurrent
    ): Promise<QueriesGetWorkforceRepresentationResponse | undefined> => {
      const selectedAnalysis = getAnalysisData(
        selection.analysisID,
        selection.proportionalAnalysisTypeID
      )

      if (!selectedAnalysis) {
        return
      }

      const payload: QueriesGetWorkforceRepresentationRequest = {
        analysisID: selectedAnalysis.analysisID,
        strataFieldGUID: selection.strataID,
        strataValuesFilter: selection.strataValue
          ? [selection.strataValue]
          : undefined,
        groupFieldGUID: undefined,
      }

      const response = await HNRV2({
        fn: () =>
          wfaAPI.apiSraDataVisualizationsWorkforceRepresentationPost(payload),
      })

      if (requestValidation && !requestValidation(selection)) {
        return
      }

      if (response instanceof AxiosError) {
        ProjectDataVisualizationsStore.displayDataVizErrorPageTakeover(
          GET_DATA_FAILED
        )
        data.value = undefined
        return
      }

      data.value = response
      return response
    }

    const getWorkforceRepresentationCharts = async (
      selection: SelectionSnapshot
    ) => {
      ProjectDataVisualizationsStore.loading.chart = true

      resetData()

      await Promise.all([getTilesAndBarCharts(selection, isCurrent)])
      ProjectDataVisualizationsStore.loading.chart = false
    }

    // const strataBackgroundColors: TBackground[] = [
    //   'dark-gray',
    //   'orange',
    //   'purple',
    //   'blue-green',
    //   'red',
    // ]
    // tiles
    function transformTopLevelTileDataToTiles(
      tileData: DataVizTileWorkforceRepresentationTileData | undefined
    ): WorkforceRepresentationTiles[] {
      if (!tileData) return []

      // Map each top-level property to a tile
      return [
        {
          title: 'Total Employees',
          numberDisplay: tileData.totalEmployees,
          icon: 'work_outline',
          background: 'blue',
          subText: `${tileData.totalDistinctStrata2}; ${tileData.totalDistinctStrata1}`,
        },
        {
          title: 'Hiring Rate',
          numberDisplay: tileData.hireRate,
          icon: 'person_add',
          background: 'purple',
          subText: `${tileData.hiredDistinctStrata1}; ${
            tileData.totalHired ?? 0
          } New Employees`,
        },
        {
          title: 'Overall Promotion Rate',
          numberDisplay: tileData.promotionRate,
          icon: 'upgrade',
          background: 'orange',
          subText: `${tileData.promotionDistinctStrata1}; ${
            tileData.totalPromoted ?? 0
          } Employees`,
        },
        {
          title: 'Turnover Rate',
          numberDisplay: tileData.turnoverRate,
          icon: 'person_remove',
          background: 'dark-gray',
          subText: `${tileData.terminatedDistinctStrata1}; ${
            tileData.totalTerminated ?? 0
          } Employees`,
        },
      ]
    }

    const tiles = computed(() => {
      return transformTopLevelTileDataToTiles(data.value?.tileData)
    })

    function transformDoughnutChartData({
      chartData,
      chartLabel,
    }: {
      chartData: QueriesWorkforceRepresentationCharts[] | undefined
      chartLabel: 'Employees'
    }) {
      if (!chartData) return []

      return [
        ...chartData.reduce<WorkforceRepresentationDoughnutChart[]>(
          (acc, curr) => {
            if (
              ProjectDataVisualizationsStore.selectedGroup &&
              curr.groupDisplayName !==
                ProjectDataVisualizationsStore.selectedGroup.groupDisplayName
            ) {
              return acc
            }
            const doughnutChartData =
              curr.workforceRepresentationOverview.reduce(
                (groupData, tableData) => {
                  tableData.data!.reduce((groupValues, currentGroup) => {
                    const datum = currentGroup.count

                    if (!dataIsDefined(datum)) {
                      return groupValues
                    }

                    groupData[tableData.axisLabel] =
                      groupData[tableData.axisLabel] || []
                    groupData[tableData.axisLabel].push(datum || 0)

                    return groupValues
                  }, {})

                  return groupData
                },
                {} as Record<string, number[]>
              )

            const datasetLabels = Object.keys(doughnutChartData).sort()

            const datasets = [
              {
                label: chartLabel,
                data: datasetLabels.map((group) => {
                  return doughnutChartData[group].slice(-1)[0]
                }),
              },
            ]

            if (!hasData(datasets)) {
              return acc
            }

            const protectedClass = curr.groupDisplayName
            const executiveSummaryId = `EeCount${protectedClass}`

            acc.push({
              id: acc.length,
              // category: 'Overview',
              protectedClass,
              chart: {
                data: {
                  labels: datasetLabels,
                  datasets,
                },
              },
              executiveSummaryId,
            } as WorkforceRepresentationDoughnutChart)

            return acc
          },
          []
        ),
      ]
    }

    function transformBarChartData<T>({
      chartData,
      listProperty,
      chartLabel,
    }: {
      chartData: WorkforceRepresentationData[] | undefined
      listProperty: WorkforceRepresentationListProperty
      chartLabel: string | undefined
    }): T[] {
      if (!chartData) return []

      return [
        ...chartData.reduce<T[]>((acc, curr) => {
          const labels = [] as string[]

          if (
            listProperty !== 'OverallTenure' &&
            ProjectDataVisualizationsStore.selectedGroup &&
            curr.groupDisplayName !==
              ProjectDataVisualizationsStore.selectedGroup.groupDisplayName
          ) {
            return acc
          }

          const barChartData = [
            ...(curr[
              `workforceRepresentation${listProperty}` as keyof Pick<
                (typeof chartData)[number],
                `workforceRepresentation${typeof listProperty}`
              >
            ] ?? []),
          ].reduce((groupData, tableData) => {
            tableData.data.reduce((groupValues, currentGroup) => {
              const getGroupLabel = ({
                listProperty,
                currentGroup,
              }: {
                listProperty: WorkforceRepresentationListProperty
                currentGroup: QueriesWorkforceChartGroupData
              }) => {
                if (listProperty === 'PayDecile') {
                  const decileValues: QueriesDecileSalaryRanges | undefined =
                    curr.decileSalaryRanges?.find(
                      (range) => currentGroup.barLabel === range.decileLabel
                    )

                  if (decileValues) {
                    return `${
                      decileValues.minimumSalary
                        ? formatAsCurrency(decileValues.minimumSalary)
                        : 'N/A'
                    } - ${
                      decileValues.maximumSalary
                        ? formatAsCurrency(decileValues.maximumSalary)
                        : 'N/A'
                    }`
                  }
                }

                return currentGroup.barLabel
              }

              const datum = [currentGroup.count, currentGroup.percent]

              if (!datum.some((value) => dataIsDefined(value))) {
                return groupValues
              }

              const groupLabel = getGroupLabel({ listProperty, currentGroup })
              if (!labels.includes(groupLabel)) {
                labels.push(groupLabel)
              }

              groupData[tableData.axisLabel] =
                groupData[tableData.axisLabel] || []
              groupData[tableData.axisLabel].push(datum)
              return groupValues
            }, {})

            return groupData
          }, {} as Record<string, ([number, number] | null | undefined)[]>)

          const datasets = Object.keys(barChartData)
            .sort()
            .map((group) => {
              return {
                label: group,
                data: barChartData[group],
              }
            })

          if (!hasData(datasets)) {
            return acc
          }

          const getExecutiveSummaryId = ({
            listProperty,
            protectedClass,
          }: {
            listProperty: string
            protectedClass: string
          }) => {
            let prefix: string

            switch (listProperty) {
              case 'OverallTenure':
                prefix = 'WorkforceTenure'
                break
              case 'Tenure':
                prefix = 'TenureBy'
                break
              default:
                prefix = listProperty
            }

            return `${prefix}${protectedClass}`
          }

          const protectedClass = curr.groupDisplayName
          const executiveSummaryId = getExecutiveSummaryId({
            listProperty,
            protectedClass,
          })

          acc.push({
            id: curr,
            category: chartLabel,
            protectedClass,
            chart: {
              data: {
                labels: labels,
                datasets,
              },
            },
            executiveSummaryId,
          } as T)

          return acc
        }, []),
      ]
    }

    const transformOverallTenureChart = (
      response: QueriesGetWorkforceRepresentationResponse
    ) => {
      return transformBarChartData<WorkforceRepresentationBarChart>({
        chartData: (response.workforceRepresentationOverallTenure
          ? [
              {
                groupDisplayName: '',
                workforceRepresentationOverallTenure:
                  response.workforceRepresentationOverallTenure,
              },
            ]
          : []) as any,
        listProperty: 'OverallTenure',
        chartLabel: 'Overall Tenure',
      })
    }

    const overallTenureChart = computed<WorkforceRepresentationBarChart[]>(
      () => {
        return data.value ? transformOverallTenureChart(data.value) : []
      }
    )

    const overviewCharts = computed<WorkforceRepresentationStackedBarChart[]>(
      () => {
        return transformBarChartData<WorkforceRepresentationStackedBarChart>({
          chartData: data.value?.workforceRepresentationCharts,
          listProperty: 'Overview',
          chartLabel: undefined,
        })
      }
    )

    const transformOverviewDoughnutCharts = (
      response: QueriesGetWorkforceRepresentationResponse
    ) => {
      return transformDoughnutChartData({
        chartData: response.workforceRepresentationCharts,
        chartLabel: 'Employees',
      })
    }

    const overviewDoughnutCharts = computed<
      WorkforceRepresentationDoughnutChart[]
    >(() => {
      return data.value ? transformOverviewDoughnutCharts(data.value) : []
    })

    const transformTenureCharts = (
      response: QueriesGetWorkforceRepresentationResponse
    ) => {
      return transformBarChartData<WorkforceRepresentationStackedBarChart>({
        chartData: response.workforceRepresentationCharts,
        listProperty: 'Tenure',
        chartLabel: 'Tenure',
      })
    }

    const tenureCharts = computed<WorkforceRepresentationStackedBarChart[]>(
      () => {
        return data.value ? transformTenureCharts(data.value) : []
      }
    )

    const transformPayGradeCharts = (
      response: QueriesGetWorkforceRepresentationResponse
    ) => {
      return transformBarChartData<WorkforceRepresentationBarChart>({
        chartData: response?.workforceRepresentationCharts,
        listProperty: 'PayGrade',
        chartLabel: 'Pay Grade',
      })
    }

    const payGradeCharts = computed<WorkforceRepresentationBarChart[]>(() => {
      return data.value ? transformPayGradeCharts(data.value) : []
    })

    const transformPayDecileCharts = (
      response: QueriesGetWorkforceRepresentationResponse
    ) => {
      return transformBarChartData<WorkforceRepresentationBarChart>({
        chartData: response.workforceRepresentationCharts,
        listProperty: 'PayDecile',
        chartLabel: 'Pay Decile',
      })
    }

    const payDecileCharts = computed<WorkforceRepresentationBarChart[]>(() => {
      return data.value ? transformPayDecileCharts(data.value) : []
    })

    const secondLowestStrataCharts = computed<
      WorkforceRepresentationBarChart[]
    >(() => {
      return transformBarChartData<WorkforceRepresentationBarChart>({
        chartData: data.value?.workforceRepresentationCharts,
        listProperty: 'SecondLowest',
        chartLabel: data.value?.secondLowestStrataName,
      })
    })

    useChartRefresh({
      fetchChartFunction: getWorkforceRepresentationCharts,
      routeName: ROUTE_NAME.WORKFORCE_INSIGHTS_DATA_VISUALIZATIONS,
    })

    const resetData = () => {
      data.value = undefined
    }

    return {
      data,
      resetData,

      tiles,
      transformTopLevelTileDataToTiles,
      transformOverviewDoughnutCharts,
      transformOverallTenureChart,
      transformTenureCharts,
      transformPayGradeCharts,
      transformPayDecileCharts,

      overviewCharts,
      overallTenureChart,
      overviewDoughnutCharts,
      tenureCharts,
      payGradeCharts,
      payDecileCharts,
      secondLowestStrataCharts,

      getTilesAndBarCharts,
    }
  }
)
